import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Link, useNavigate } from 'react-router-dom';

import { socketUseEffect } from "../../socket";
import { socket } from "../../socket";
import BasicCRUD from './../core/basic-crud';
import BadgeStatusView from "./sub-pages/elements/BadgeStatusView";
import { useAuth } from "../../AuthContext";
import ProgressBar from "../page-elements/progress-bar";
import FadeAlert from "../core/fade-alert";
import resolveUserRole from "../utils/resolveUserRole";

// ===== i18n =====
const dict = {
    en: {
        title: "Domains",
        subTitle: "add / edit domains",
        homeURL: "/dashboard",
        addTitle: "Add Domain",
        editTitle: "Edit Domain",

        colStatus: "Status",
        colDomainName: "Domain Name",
        colRegistrator: "Registrar",
        colContactGroup: "Contact Group",

        unknownDomain: "Unknown Domain",
        expiration: "Expiration:",
        expired: "Expired",
        invalidDate: "Invalid Date",

        mainTab: "Main",
        fieldDomainName: "Domain Name",
        fieldContactGroup: "Contact Group",
        choose: " -- choose -- ",

        edit: "Edit",
        delete: "Delete",

        noResources: "You don't have any available resources. Please change your pricing plan to get more items.",
        planExpired: "Your pricing plan has expired. Please renew or upgrade your plan to continue accessing this feature.",

        delAlert: "Deleting a domain cannot be undone.",
        delText: "To confirm deletion, enter the name of the domain in the next input field",
        delTitle: 'Delete domain: "{name}"',

        toastEntity: "Domain",
        createdOk: "created successfully",
        updatedOk: "updated successfully",
        deletedOk: "deleted successfully",
        memberViewOnly: "Members can only view domains.",

        // plural helpers
        day: (n) => `day${n === 1 ? "" : "s"}`,
        hour: (n) => `hour${n === 1 ? "" : "s"}`,
    },
    ru: {
        title: "Домены",
        subTitle: "добавление / редактирование доменов",
        homeURL: "/dashboard",
        addTitle: "Добавить домен",
        editTitle: "Редактировать домен",

        colStatus: "Статус",
        colDomainName: "Имя домена",
        colRegistrator: "Регистратор",
        colContactGroup: "Группа контактов",

        unknownDomain: "Неизвестный домен",
        expiration: "Окончание срока:",
        expired: "Истёк",
        invalidDate: "Неверная дата",

        mainTab: "Основное",
        fieldDomainName: "Имя домена",
        fieldContactGroup: "Группа контактов",
        choose: " -- выберите -- ",

        edit: "Редактировать",
        delete: "Удалить",

        noResources: "У вас нет доступных ресурсов. Пожалуйста, измените тарифный план, чтобы получить больше элементов.",
        planExpired: "Срок действия вашего тарифного плана истёк. Обновите или продлите план, чтобы продолжить пользоваться функцией.",

        delAlert: "Удаление домена необратимо.",
        delText: "Чтобы подтвердить удаление, введите имя домена в следующее поле ввода",
        delTitle: 'Удалить домен: «{name}»',

        toastEntity: "Домен",
        createdOk: "успешно создан",
        updatedOk: "успешно обновлён",
        deletedOk: "успешно удалён",
        memberViewOnly: "У участников нет прав управлять доменами.",

        day: (n) => plural(n, { one: "день", few: "дня", many: "дней" }),
        hour: (n) => plural(n, { one: "час", few: "часа", many: "часов" }),
    },
    ua: {
        title: "Домени",
        subTitle: "додавання / редагування доменів",
        homeURL: "/dashboard",
        addTitle: "Додати домен",
        editTitle: "Редагувати домен",

        colStatus: "Статус",
        colDomainName: "Назва домену",
        colRegistrator: "Реєстратор",
        colContactGroup: "Група контактів",

        unknownDomain: "Невідомий домен",
        expiration: "Закінчення строку:",
        expired: "Строк минув",
        invalidDate: "Невірна дата",

        mainTab: "Основне",
        fieldDomainName: "Назва домену",
        fieldContactGroup: "Група контактів",
        choose: " -- оберіть -- ",

        edit: "Редагувати",
        delete: "Видалити",

        noResources: "У вас немає доступних ресурсів. Будь ласка, змініть тарифний план, щоб отримати більше елементів.",
        planExpired: "Строк дії вашого тарифного плану минув. Поновіть або оновіть план, щоб продовжити користування функцією.",

        delAlert: "Видалення домену неможливо скасувати.",
        delText: "Щоб підтвердити видалення, введіть назву домену у наступне поле вводу",
        delTitle: 'Видалити домен: «{name}»',

        toastEntity: "Домен",
        createdOk: "успішно створено",
        updatedOk: "успішно оновлено",
        deletedOk: "успішно видалено",
        memberViewOnly: "У учасників немає прав керувати доменами.",

        day: (n) => plural(n, { one: "день", few: "дні", many: "днів" }),
        hour: (n) => plural(n, { one: "година", few: "години", many: "годин" }),
    },
};

const plural = (n, { one, few, many }) => {
    const n10 = n % 10, n100 = n % 100;
    if (n10 === 1 && n100 !== 11) return one;
    if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return few;
    return many;
};

const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

// ===== component =====
const Domains = ({ lang: langProp, ...props }) => {
    const lang = (langProp || getInitialLang());
    const t = useMemo(() => {
        // bind plural helpers if needed
        const base = dict[lang] || dict.en;
        return base;
    }, [lang]);

    const object = "domains";
    const [response, setResponse] = useState({});
    const [fieldsData, setFieldsData] = useState({});
    const [afterDeletePath, setAfterDeletePath] = useState("/domains");
    const navigate = useNavigate();
    const [contacts, setContacts] = useState([{ id: 0, name: (dict[lang]?.choose || dict.en.choose) }]);
    const [totalData, setTotalData] = useState([]);

    const { user } = useAuth();
    const currentUserRole = useMemo(() => resolveUserRole(user), [user]);
    const canManageDomains = currentUserRole !== "member";
    const listeners = [
        { name: "contacts", fn: "items-list", args: { user_id: user.user_id, site_id: props.site_id }, saveDataFN: setContacts },
        { name: "dashboard", fn: "reports-total", args: {}, saveDataFN: setTotalData },
    ];
    useEffect(() => socketUseEffect(listeners, user), [response?.status, response?.created, response?.updated, response?.delete]);

    const formatStatus = (data) => <BadgeStatusView status={data['status']} lang={lang} />;

    // After successful deletion in single view, compute next redirect target
    useEffect(() => {
        if (!(props.singleArgs?.delete) || response?.delete !== 1) return;
        const eventName = `${object}:get-items-list-data`;
        const handler = (payload) => {
            socket.off(eventName, handler);

            const list =
                (payload && Array.isArray(payload.data) && payload.data)
                || (Array.isArray(payload) ? payload : [])
                || (Array.isArray(payload?.[1]) ? payload[1] : []);

            const currentId = props.singleArgs?.id || props.singleArgs?.details?.id;
            const candidates = (list || []).filter((row) => Number(row.id) !== 0 && Number(row.id) !== Number(currentId));
            const nextId = candidates?.[0]?.id;
            setAfterDeletePath(nextId ? `/domains/${nextId}/dashboard` : "/domains");
        };
        socket.on(eventName, handler);
        socket.emit(`${object}:get-items-list`, {
            token: user.token,
            simple_search_text: "",
            order_by: "domain_name",
            order_rule: "asc",
        });
        return () => socket.off(eventName, handler);
    }, [response?.delete, props.singleArgs?.delete, user.token]);

    const getExpirationMeta = (data) => {
        const expirationDate = new Date(data['date_expiration']);
        const formattedExpirationDate = isNaN(expirationDate)
            ? t.invalidDate
            : expirationDate
                .toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                })
                .replace(',', '');

        const now = moment();
        const expiration = moment(data['date_expiration']);
        const diff = moment.duration(expiration.diff(now));

        let daysLeftText = t.expired;
        let color = '#f20000';

        if (diff.asMilliseconds() > 0) {
            const days = Math.floor(diff.asDays());
            const hours = diff.hours();

            if (days <= 1) color = '#f20000';
            else if (days <= 10) color = '#ff9a44';
            else color = '#4CAF50';

            const dayLabel = t.day(days);
            const hourLabel = t.hour(hours);

            daysLeftText = `${days} ${dayLabel}${hours > 0 ? ` ${hours} ${hourLabel}` : ''}`;
        }

        return { formattedExpirationDate, daysLeftText, color };
    };

    const formatDomainDetails = (data) => {
        const domainName = data['domain_name'] || t.unknownDomain;
        const { formattedExpirationDate, daysLeftText, color } = getExpirationMeta(data);

        const styles = {
            container: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' },
            domainName: { fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' },
            expirationAndDaysLeft: { fontSize: '12px', color: '#333' },
            daysLeft: { color, fontWeight: 'bold' },
        };

        const domainsUrl = `/domains/${data.id}/dashboard`;

        return (
            <div style={styles.container}>
                <Link style={styles.domainName} to={domainsUrl}>
                    {domainName}
                </Link>
                <span style={styles.expirationAndDaysLeft}>
          {t.expiration} {String(data['status']) === "0"
                    ? (<> - </>)
                    : (<>{formattedExpirationDate} — <span style={styles.daysLeft}>{daysLeftText}</span></>)}
        </span>
            </div>
        );
    };

    const columns = [
        { label: t.colStatus, key: "status", format: formatStatus },
        { label: t.colDomainName, key: "domain_name", format: formatDomainDetails },
        { label: t.colRegistrator, key: "registrator" },
        { label: t.colContactGroup, key: "contacts_group_name" },
    ];

    const fields = {
        add: [
            {
                type: "tab",
                name: t.mainTab,
                image: "",
                fields: [
                    [{ label: t.fieldDomainName, key: "domain_name", default_value: "", showLabel: "top", readonly: false }],
                    [{ label: t.fieldContactGroup, key: "contact_group_id", default_value: "", type: "selectbox", data: contacts, showLabel: "top" }],
                ],
            }
        ],
        edit: [
            {
                type: "tab",
                name: t.mainTab,
                image: "",
                fields: [
                    [{ label: t.fieldDomainName, key: "domain_name", default_value: "", showLabel: "top", readonly: true }],
                    [{ label: t.fieldContactGroup, key: "contact_group_id", default_value: "", type: "selectbox", data: contacts, showLabel: "top" }],
                ],
            }
        ],
    };

    const actions = canManageDomains ? [
        { name: "edit", label: t.edit, controller: "crud", icon: "ti ti-edit" },
        { name: "delete", label: t.delete, controller: "crud", icon: "ti ti-trash" },
    ] : [];

    const expired = user?.expired ?? 1;
    const disabled = !canManageDomains
        ? { add: { disable: true, title: t.memberViewOnly } }
        : (expired
            ? { add: { disable: true, title: t.planExpired } }
            : {
                add: {
                    disable: (totalData.domains - totalData.used_domains) < 1,
                    title: t.noResources,
                }
            });

    const renderDomainCard = (item, helpers) => {
        const { formattedExpirationDate, daysLeftText, color } = getExpirationMeta(item);
        const registrar = item.registrator || t.unknownDomain;
        const contactGroup = item.contacts_group_name || t.unknownDomain;
        const actionsList = helpers.actions || [];

        return (
            <Link to={`/domains/${item.id}/dashboard`} className="text-decoration-none text-dark">
                <div className="card h-100 shadow-sm border">
                    <div className="card-body d-flex flex-column">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <div>{formatStatus(item)}</div>
                        <span className="badge rounded-pill" style={{ backgroundColor: color, color: "#fff" }}>
                            {daysLeftText}
                        </span>
                    </div>

                    <div className="mb-3">
                        <div
                            className="d-inline-block fw-bold text-decoration-none text-dark"
                            style={{ fontSize: 22, marginBottom: 4 }}
                        >
                            {item.domain || item.domain_name || t.noName}
                        </div>
                        <div className="fw-semibold text-muted small">{formattedExpirationDate}</div>
                    </div>

                    <div className="mb-2">
                        <div className="text-muted text-uppercase fw-semibold fs-2">{t.colRegistrator}</div>
                        <div className="fw-semibold">{registrar}</div>
                    </div>

                    <div className="mb-4">
                        <div className="text-muted text-uppercase fw-semibold fs-2">{t.colContactGroup}</div>
                        <div className="fw-semibold">{contactGroup}</div>
                    </div>

                    <div className="text-muted small mb-2">{formattedExpirationDate}</div>

                    {actionsList.length > 0 && (
                        <div className="mt-auto d-flex gap-2 justify-content-end flex-wrap">
                            {actionsList.map((action) => (
                                <button
                                    key={action.name}
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    title={action.label}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        helpers.tableAction?.(action.name, item);
                                    }}
                                >
                                    <i className={action.icon || "ti ti-dots"}></i>
                                </button>
                            ))}
                        </div>
                    )}
                    </div>
                </div>
            </Link>
        );
    };

    return (
        <>
            <BasicCRUD
                singleArgs={props?.singleArgs ?? false}
                title={t.title}
                subTitle={t.subTitle}
                homeURL={t.homeURL}
                addTitle={t.addTitle}
                disable={disabled}
                editTitle={t.editTitle}
                object={object}
                columns={columns}
                fields={fields}
                actions={actions}
                fieldsData={fieldsData}
                setFieldsData={setFieldsData}
                response={response}
                setResponse={setResponse}
                ownHandler={true}
                afterDeleteRedirect={afterDeletePath}
                deleteConfirmationDialog={{
                    alert: t.delAlert,
                    text: t.delText,
                    title: t.delTitle,
                    column: 'domain_name',
                }}
                viewModes={["table", "blocks"]}
                renderItem={renderDomainCard}
                cardLayout={{ columnClass: "col-12 col-lg-6 col-xxl-4" }}
            >
                {!props.singleArgs && (
                    <ProgressBar lang={lang} used={totalData.used_domains} allowed={totalData.domains} />
                )}

                {response.status === 1 ? (
                    <FadeAlert response={response} resetResponse={() => setResponse([])}>
                        {response.created === 1 && (<><strong>{t.toastEntity}</strong> {t.createdOk}</>)}
                        {response.updated === 1 && (<><strong>{t.toastEntity}</strong> {t.updatedOk}</>)}
                        {response.delete === 1 && (<><strong>{t.toastEntity}</strong> {t.deletedOk}</>)}
                    </FadeAlert>
                ) : null}
            </BasicCRUD>
        </>
    );
};

export default Domains;
